// apps/widget/src/lib/face-regions.ts
//
// Turns a metric's facial regions into shapes on the captured photo.
//
// What this draws is WHERE a score is about — the under-eye area for dark
// circles, the T-zone for oiliness — not individual detections. The analysis
// returns a severity per metric, not the position of each spot or line, and
// drawing dots as if they were found features would be inventing findings on
// someone's face.

import type { FaceRegion } from '@skinvizo/shared';
import { REGION_LANDMARKS } from './facemesh';

type Point = { x: number; y: number };

/** Regions whose landmark lists cover both sides of the face. */
const BILATERAL = new Set<FaceRegion>([
  'periorbital',
  'infraorbital',
  'upperEyelid',
  'lowerEyelid',
  'nasolabial',
]);

/** Areas drawn as the union of others. The T-zone as one hull was a triangle
 *  from the temples to the nose tip, straight across both eyes. */
const COMPOSITE: Partial<Record<FaceRegion, FaceRegion[]>> = {
  tzone: ['forehead', 'nose'],
};

/** MediaPipe's nose-tip landmark: the face's vertical midline. */
const NOSE_TIP = 1;

export interface RegionShape {
  d: string;
  closed: boolean;
}

export function regionShapes(
  landmarks: Point[],
  region: FaceRegion,
  width: number,
  height: number,
): RegionShape[] {
  const parts = COMPOSITE[region];
  if (parts) return parts.flatMap((r) => regionShapes(landmarks, r, width, height));

  const indices = REGION_LANDMARKS[region];
  if (!indices) return [];

  const points = indices
    .map((i) => landmarks[i])
    .filter((p): p is Point => p !== undefined)
    .map((p) => ({ x: p.x * width, y: p.y * height }));
  if (points.length < 3) return [];

  // The jaw is a line along the edge of the face, not an area.
  if (region === 'jawline') {
    const sorted = [...points].sort((a, b) => a.x - b.x);
    return [{ d: smoothOpen(sorted), closed: false }];
  }

  const midline = (landmarks[NOSE_TIP]?.x ?? 0.5) * width;
  const groups = BILATERAL.has(region)
    ? [points.filter((p) => p.x < midline), points.filter((p) => p.x >= midline)]
    : [points];

  return groups
    .filter((g) => g.length >= 3)
    .map((g) => ({ d: smoothClosed(hull(g)), closed: true }));
}

/** The box that frames the face, padded and shaped for a portrait panel. */
export function faceFrame(landmarks: Point[], width: number, height: number) {
  const xs = landmarks.map((p) => p.x * width);
  const ys = landmarks.map((p) => p.y * height);
  const minX = Math.min(...xs), maxX = Math.max(...xs);
  const minY = Math.min(...ys), maxY = Math.max(...ys);
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;

  // Face fills about half the frame height, as in a portrait, with a little
  // more room below the chin than above the forehead for the scores.
  let h = Math.min(height, (maxY - minY) * 2);
  let w = Math.min(width, h * 0.68);
  if (w === width) h = Math.min(height, w / 0.68);

  const x = Math.max(0, Math.min(width - w, cx - w / 2));
  const y = Math.max(0, Math.min(height - h, cy - h * 0.46));
  return { x, y, w, h };
}

// ── geometry ────────────────────────────────────────────────────────────────

/** Convex hull, monotone chain. The landmark lists are not in perimeter order. */
function hull(points: Point[]): Point[] {
  const p = [...points].sort((a, b) => a.x - b.x || a.y - b.y);
  const cross = (o: Point, a: Point, b: Point) =>
    (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const lower: Point[] = [];
  for (const q of p) {
    while (lower.length >= 2 && cross(lower[lower.length - 2]!, lower[lower.length - 1]!, q) <= 0) lower.pop();
    lower.push(q);
  }
  const upper: Point[] = [];
  for (const q of [...p].reverse()) {
    while (upper.length >= 2 && cross(upper[upper.length - 2]!, upper[upper.length - 1]!, q) <= 0) upper.pop();
    upper.push(q);
  }
  return lower.slice(0, -1).concat(upper.slice(0, -1));
}

/** A rounded closed outline through the hull, so it reads as an area, not a polygon. */
function smoothClosed(pts: Point[]): string {
  const mid = (a: Point, b: Point) => ({ x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 });
  const start = mid(pts[pts.length - 1]!, pts[0]!);
  let d = `M${start.x.toFixed(1)},${start.y.toFixed(1)}`;
  pts.forEach((p, i) => {
    const m = mid(p, pts[(i + 1) % pts.length]!);
    d += ` Q${p.x.toFixed(1)},${p.y.toFixed(1)} ${m.x.toFixed(1)},${m.y.toFixed(1)}`;
  });
  return d + ' Z';
}

function smoothOpen(pts: Point[]): string {
  let d = `M${pts[0]!.x.toFixed(1)},${pts[0]!.y.toFixed(1)}`;
  for (let i = 1; i < pts.length - 1; i++) {
    const m = { x: (pts[i]!.x + pts[i + 1]!.x) / 2, y: (pts[i]!.y + pts[i + 1]!.y) / 2 };
    d += ` Q${pts[i]!.x.toFixed(1)},${pts[i]!.y.toFixed(1)} ${m.x.toFixed(1)},${m.y.toFixed(1)}`;
  }
  const last = pts[pts.length - 1]!;
  return d + ` L${last.x.toFixed(1)},${last.y.toFixed(1)}`;
}
